import React, { useMemo } from 'react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import { money } from '../treasury/treasuryUi';

const PAYMENT_LAG = { contado: 0, credito_30: 1, credito_45: 2 };
const PAYMENT_LABEL = {
  contado: 'Contado',
  credito_30: 'Crédito 30 días',
  credito_45: 'Crédito 45 días',
};

const zeros = (count) => Array.from({ length: Math.max(1, count) }, () => 0);

const addSeries = (left = [], right = [], count) => {
  const n = Math.max(count || 0, left.length, right.length, 1);
  return Array.from({ length: n }, (_, index) => Number(left[index] || 0) + Number(right[index] || 0));
};

const shiftSeries = (months = [], lag = 0, count) => {
  const n = Math.max(1, count || months.length || 1);
  const out = zeros(n);
  (months || []).forEach((value, index) => {
    const amount = Number(value || 0);
    if (!amount) return;
    const target = index + Number(lag || 0);
    if (target < 0 || target >= n) return;
    out[target] += amount;
  });
  return out;
};

const lagOf = (item) => PAYMENT_LAG[item?.payment_term] ?? 0;

const sumItems = (items, count) =>
  (items || []).reduce((acc, item) => addSeries(acc, shiftSeries(item.months, lagOf(item), count), count), zeros(count));

const formatCompact = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toLocaleString('es-CL', { maximumFractionDigits: 1 })} MM`;
  if (abs >= 1e5) return `$${(n / 1e3).toLocaleString('es-CL', { maximumFractionDigits: 0 })} mil`;
  return money(n);
};

const BudgetCashFlowOverview = ({ budget = null, monthAxis = [], ui, loading = false }) => {
  const monthCount = Math.max(1, monthAxis.length || Number(budget?.month_count) || 12);
  const headers = (monthAxis || []).map((item) => item?.label || item?.short_label || `${item?.month || ''}/${item?.year || ''}`);
  const model = useMemo(() => {
    const inflows = sumItems(budget?.sales_items, monthCount);
    const materials = sumItems(budget?.product_costs, monthCount);
    const costs = sumItems(
      [...(budget?.cost_items || []), ...(budget?.variable_items || [])],
      monthCount
    );
    const outflows = addSeries(materials, costs, monthCount);
    const net = inflows.map((value, index) => Number(value || 0) - Number(outflows[index] || 0));
    let running = 0;
    const cumulative = net.map((value) => {
      running += Number(value || 0);
      return running;
    });
    const byTerm = { contado: 0, credito_30: 0, credito_45: 0 };
    (budget?.sales_items || []).forEach((item) => {
      const term = item.payment_term || 'contado';
      if (byTerm[term] == null) byTerm[term] = 0;
      byTerm[term] += Number(item.budget_total || 0);
    });
    return {
      inflows,
      materials,
      costs,
      outflows,
      net,
      cumulative,
      inflowTotal: inflows.reduce((acc, value) => acc + Number(value || 0), 0),
      outflowTotal: outflows.reduce((acc, value) => acc + Number(value || 0), 0),
      netTotal: net.reduce((acc, value) => acc + Number(value || 0), 0),
      cashEnd: cumulative[cumulative.length - 1] || 0,
      byTerm,
    };
  }, [budget, monthCount]);

  const rows = [
    { key: 'in', label: 'Cobros', months: model.inflows, total: model.inflowTotal },
    { key: 'mp', label: 'Pagos materias primas', months: model.materials, total: model.materials.reduce((acc, value) => acc + Number(value || 0), 0) },
    { key: 'op', label: 'Pagos costos y gastos', months: model.costs, total: model.costs.reduce((acc, value) => acc + Number(value || 0), 0) },
    { key: 'out', label: 'Pagos totales', months: model.outflows, total: model.outflowTotal, strong: true },
    { key: 'net', label: 'Flujo neto', months: model.net, total: model.netTotal, strong: true },
    { key: 'cash', label: 'Caja acumulada', months: model.cumulative, total: model.cashEnd, strong: true },
  ];

  const th = {
    textAlign: 'right',
    padding: '8px 6px',
    borderBottom: `1px solid ${ui.border}`,
    fontSize: 11,
    color: ui.muted,
    whiteSpace: 'nowrap',
    background: ui.header,
  };

  if (loading) {
    return <p style={{ margin: 0, padding: '24px 12px', fontSize: 13, color: ui.muted }}>Calculando…</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        {[
          { label: 'Cobros', value: model.inflowTotal, hint: Object.entries(model.byTerm)
            .filter(([, amount]) => amount)
            .map(([term, amount]) => `${PAYMENT_LABEL[term] || term} ${formatCompact(amount)}`)
            .join(' · ') || 'Según forma de pago de las ventas' },
          { label: 'Pagos', value: model.outflowTotal, hint: 'Costos, gastos y materias primas' },
          { label: 'Flujo neto', value: model.netTotal, hint: 'Cobros − pagos del periodo' },
          { label: 'Caja acumulada', value: model.cashEnd, hint: 'Saldo al cierre del horizonte' },
        ].map((card) => (
          <div
            key={card.label}
            style={{
              background: ui.header,
              border: `1px solid ${ui.border}`,
              borderRadius: 10,
              padding: '12px 14px',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {card.label}
            </div>
            <div style={{ marginTop: 6, fontSize: 20, fontWeight: 800, color: ui.text }}>{formatCompact(card.value)}</div>
            <div style={{ marginTop: 4, fontSize: 11, color: ui.muted }}>{card.hint}</div>
          </div>
        ))}
      </div>
      <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
        El desfase usa la forma de pago de cada ítem: Contado en el mismo mes, Crédito 30 días al mes siguiente y Crédito 45 días a dos meses.
      </p>
      <div style={{ overflowX: 'auto', background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 240 + headers.length * 88 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', minWidth: 180 }}>Concepto</th>
              {headers.map((label, index) => (
                <th key={`${label}-${index}`} style={th}>
                  {label || MONTH_LABELS[index]}
                </th>
              ))}
              <th style={th}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td style={{ padding: '8px 10px', fontSize: 12, fontWeight: row.strong ? 700 : 500, color: ui.text }}>
                  {row.label}
                </td>
                {(row.months || []).map((value, index) => (
                  <td
                    key={`${row.key}-${index}`}
                    style={{
                      padding: '8px 6px',
                      textAlign: 'right',
                      fontSize: 12,
                      fontWeight: row.strong ? 700 : 500,
                      color: Number(value) < 0 ? ui.danger || '#b91c1c' : ui.text,
                    }}
                  >
                    {money(value)}
                  </td>
                ))}
                <td
                  style={{
                    padding: '8px 6px',
                    textAlign: 'right',
                    fontSize: 12,
                    fontWeight: 700,
                    color: Number(row.total) < 0 ? ui.danger || '#b91c1c' : ui.text,
                  }}
                >
                  {money(row.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BudgetCashFlowOverview;
